import { useState, useEffect } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { datesFilterChange } from "modules/Campaigns/CampaignAssets/actions";
import { RootState } from "store";
import { FromUTCEpochToMonthDayString } from "../../../utils";

interface IScalerProps {
  gradients: any;
}
const colors = ["rgba(255, 150, 45,1)", "rgb(139, 165, 230)", "rgb(211, 27, 27)", "rgb(24, 195, 81)"];
const formatData = (data: any, gradients: any) => {
  let pDatasets: any = [];
  let xAxesDateLabels: any = [];
  let legendLabels: any = [];
  const epochsMap = new Map();

  let utcEpochs: Array<number> = Array.from(
    new Set(
      data.campaigns_series?.reduce((accumulator: any, dataset: any) => {
        legendLabels.push(dataset.campaign_name);
        return [
          ...accumulator,
          ...dataset.events_data.map((dataPoint: any) => {
            return dataPoint[0];
          }),
        ];
      }, [])
    )
  );
  utcEpochs = utcEpochs?.sort((a: any, b: any) => a - b);

  const xAxesLabels: Array<number> = utcEpochs;
  xAxesLabels?.forEach((epoch, index) => {
    epochsMap.set(epoch, index);
  });
  const yAxesDatasets: Array<any> = [];
  data.campaigns_series?.forEach((campaign: any) => {
    const clicks: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);
    const opens: Array<any> = new Array(xAxesLabels.length).fill(0, 0, xAxesLabels.length);
    let startIndex = 99999999;
    campaign?.events_data?.forEach((datapoint: Array<number>) => {
      const epoch: number = datapoint[0];
      const ind: number = epochsMap.get(epoch);
      clicks[ind] = datapoint[1];
      opens[ind] = datapoint[2];

      if (ind < startIndex) {
        startIndex = ind;
      }
    });
    clicks.fill(null, 0, startIndex);
    opens.fill(null, 0, startIndex);

    startIndex = 99999999;

    yAxesDatasets.push({ name: campaign.campaign_name, clicks, opens });
  });
  let colorIndex = 0;
  pDatasets = yAxesDatasets?.reduce((accumulator, currentValue) => {
    accumulator.push({
      label: "Clicks " + currentValue.name,
      fill: "+1",
      backgroundColor: gradients[colorIndex],
      borderColor: colors[colorIndex],
      borderCapStyle: "butt",
      borderDashOffset: 0.0,
      borderJoinStyle: "round",
      pointBorderColor: colors[colorIndex],
      pointBackgroundColor: "white",
      pointBorderWidth: 1,
      pointHoverRadius: 5,
      pointHitRadius: 10,
      data: currentValue.clicks,
      pointStyle: "rectRot",
      spanGaps: true,
      borderDash: [0.3, 3],
    });
    accumulator.push({
      label: "Opens " + currentValue.name,
      fill: false,
      borderColor: colors[colorIndex],
      borderCapStyle: "butt",
      borderDashOffset: 0.0,
      borderJoinStyle: "round",
      pointBorderColor: colors[colorIndex],
      pointBackgroundColor: colors[colorIndex],
      pointBorderWidth: 1,
      pointHoverRadius: 5,
      pointHitRadius: 10,
      data: currentValue.opens,
      pointStyle: "triangle",
      spanGaps: true,
      borderDash: [0.3, 3],
    });
    colorIndex++;
    return accumulator;
  }, []);
  xAxesDateLabels = ToMonthDayLables(xAxesLabels);
  return {
    xAxesDateLabels,
    pDatasets,
    legendLabels,
  };
};

const ToMonthDayLables = (labels: Array<number>) => {
  return labels.map((epoch: any) => {
    return FromUTCEpochToMonthDayString(epoch);
  });
};

export const useGraphDataScaler = ({ gradients }: IScalerProps) => {
  const [plotData, setPlotData] = useState({
    pDatasets: [],
    xAxesDateLabels: [],
  });
  const [rawData, setRawData] = useState({});
  const [colorGradients, setColorGradients] = useState(gradients);
  const data = useSelector((state: RootState) => {
    return state.campaignsGraphData.data;
  }, shallowEqual);

  const start_date = useSelector((state: RootState) => state.campaignsGraphData.startDate);
  const end_date = useSelector((state: RootState) => state.campaignsGraphData.endDate);

  useEffect(() => {
    if (data && Object.keys(data).length !== 0) {
      setRawData(data);
    }
  }, [data]);

  useEffect(() => {
    if (rawData && Object.keys(rawData).length !== 0 && colorGradients?.length > 0) {
      const c = formatData(rawData, colorGradients);
      setPlotData(c);
    }
  }, [rawData, colorGradients]);

  useEffect(() => {
    if (start_date === "" || end_date === "") {
      datesFilterChange({ value: "qtr", label: "1 QTR" });
    }
  }, []);

  return [plotData, setColorGradients];
};

export const useEmailGraphDataScaler = ({ gradients }: IScalerProps) => {
  const [plotData, setPlotData] = useState({
    pDatasets: [],
    xAxesDateLabels: [],
  });
  const [rawData, setRawData] = useState({});
  const [colorGradients, setColorGradients] = useState(gradients);
  const data = useSelector((state: RootState) => {
    return state.emails.graph.data;
  }, shallowEqual);

  const start_date = useSelector((state: RootState) => state.emails.graph.startDate);
  const end_date = useSelector((state: RootState) => state.emails.graph.endDate);

  useEffect(() => {
    if (data && Object.keys(data).length !== 0) {
      setRawData(data);
    }
  }, [data]);

  useEffect(() => {
    if (rawData && Object.keys(rawData).length !== 0 && colorGradients?.length > 0) {
      const c = formatData(rawData, colorGradients);
      setPlotData(c);
    }
  }, [rawData, colorGradients]);

  useEffect(() => {
    if (start_date === "" || end_date === "") {
      datesFilterChange({ value: "qtr", label: "1 QTR" });
    }
  }, []);

  return [plotData, setColorGradients];
};
